import React, { useState } from "react";
import Modal from "../utils/Modal/Modal";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as LockIcon } from "../../assets/icons/lock.svg";

import {
  ModalContainer,
  ModalHeader,
  HeaderHero,
  TitleGroup,
  ModalTitle,
  ModalSubtitle,
  CloseButton,
  ModalBody,
  Section,
  SectionLabel,
  SegmentedControl,
  SegmentButton,
  SeedInput,
  SettingsCard,
  QualityGrid,
  SettingCol,
  SettingLabel,
  Badge,
  InlineLock,
  PromptsPanel,
  PromptsHeader,
  PromptsLabel,
  PromptsChevron,
  PromptsContent,
  PromptGroup,
  PromptLabel,
  PromptTextarea,
  Footer,
  TimeBadge,
  ActionRow,
  PrimaryButton,
  PrimaryCostGroup,
  CancelLink,
} from "./EnterPromptModal.styles";
import {
  ALL_VIDEO_RESOLUTIONS,
  DEFAULT_VIDEO_DURATION,
  PRO_VIDEO_DURATION,
  VIDEO_RESOLUTION,
  VIDEO_DURATION_PRICE,
  VIDEO_RESOLUTION_PRICE,
  PRO_PLAN_NAME,
  PREMIUM_PLAN_NAME,
  BASIC_PLAN_NAME,
} from "../../utils/constants";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/user/slice";
import UnlockModal from "../UnlockModal/UnlockModal";
import { GenerateDrop } from "../CreateInputBar/CreateInputBar.styles";

const VIDEO_RESOLUTION_DESCRIPTION = {
  LOW: "2 mins",
  MEDIUM: "3-5 mins",
  HIGH: "4-10 mins",
};

const DURATION_OPTIONS = [5, 8, 10, 12];

const EnterPromptModal = ({
  isOpen,
  onClose,
  customPrompt,
  setCustomPrompt,
  negativePrompt,
  setNegativePrompt,
  videoResolution,
  setVideoResolution,
  enableVideoQuality,
  customSeed,
  setCustomSeed,
  collectionSeed,
  showPrompts,
  setShowPrompts,
  videoDuration,
  setVideoDuration,
  modelPrice,
  resetSeed,
  isMinVideoLength8sec,
  collectionId,
}) => {
  const user = useSelector(selectUser);
  const [openUnlockModal, setOpenUnlockModal] = useState(false);
  const [seedMode, setSeedMode] = React.useState(() => {
    return localStorage.getItem("seedMode") || "random";
  });

  const isProEnabled =
    [PRO_PLAN_NAME, PREMIUM_PLAN_NAME].includes(user?.subscription?.name) &&
    user?.subscription?.endDate &&
    new Date(user?.subscription?.endDate) > new Date();

  const isBasicEnabled =
    user?.subscription?.name === BASIC_PLAN_NAME &&
    user?.subscription?.endDate &&
    new Date(user?.subscription?.endDate) > new Date();

  const isSubscribed = isBasicEnabled || isProEnabled;

  const calculateTotalPrice = (modelPrice) => {
    let totalPrice = modelPrice;

    if (videoResolution && VIDEO_RESOLUTION_PRICE[videoResolution]) {
      totalPrice += VIDEO_RESOLUTION_PRICE[videoResolution];
    }

    if (videoDuration && VIDEO_DURATION_PRICE[videoDuration]) {
      totalPrice += VIDEO_DURATION_PRICE[videoDuration];
    }

    return totalPrice;
  };

  const totalPrice = calculateTotalPrice(modelPrice);

  const generateRandomSeed = () => {
    return Math.floor(Math.random() * 10000000) + 1;
  };

  React.useEffect(() => {
    const savedSeedMode = localStorage.getItem("seedMode");
    const savedCustomSeed = localStorage.getItem("customSeed");

    if (savedSeedMode) {
      setSeedMode(savedSeedMode);

      if (savedSeedMode === "random") {
        setCustomSeed(generateRandomSeed());
        return;
      }

      // When default mode, always use collectionSeed
      if (savedSeedMode === "default") {
        setCustomSeed(collectionSeed);
        return;
      }

      // When custom mode, use saved custom seed if available
      if (savedSeedMode === "custom") {
        if (savedCustomSeed) {
          setCustomSeed(Number(savedCustomSeed));
        } else {
          // Fallback for custom mode without saved seed
          setCustomSeed(collectionSeed || generateRandomSeed());
        }
        return;
      }
    }

    // Fallback if no saved mode exists
    if (collectionSeed) {
      setCustomSeed(collectionSeed);
    } else {
      setCustomSeed(generateRandomSeed());
    }
  }, [seedMode, setCustomSeed, collectionSeed, resetSeed]);

  React.useEffect(() => {
    if (!Number(localStorage.getItem("videoDuration")) && collectionId) {
      setVideoDuration(isMinVideoLength8sec ? 8 : 5);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMinVideoLength8sec, setVideoDuration, collectionId]);

  React.useEffect(() => {
    if (user) {
      if (!isSubscribed) {
        if (!isMinVideoLength8sec) {
          setVideoDuration(DEFAULT_VIDEO_DURATION);
        } else {
          if (PRO_VIDEO_DURATION.includes(videoDuration)) {
            setVideoDuration(8);
            localStorage.setItem("videoDuration", 8);
          }
        }

        setVideoResolution(VIDEO_RESOLUTION.LOW);
        localStorage.setItem("videoResolution", VIDEO_RESOLUTION.LOW);
      } else {
        if (!isProEnabled) {
          if (videoResolution === VIDEO_RESOLUTION.HIGH) {
            setVideoResolution(VIDEO_RESOLUTION.MEDIUM);
            localStorage.setItem("videoResolution", VIDEO_RESOLUTION.MEDIUM);
          }
          if (PRO_VIDEO_DURATION.includes(videoDuration)) {
            setVideoDuration(isMinVideoLength8sec ? 8 : DEFAULT_VIDEO_DURATION);
            localStorage.setItem(
              "videoDuration",
              isMinVideoLength8sec ? 8 : DEFAULT_VIDEO_DURATION,
            );
          }
        }
      }
    }
  }, [
    isSubscribed,
    isProEnabled,
    setVideoResolution,
    setVideoDuration,
    videoResolution,
    videoDuration,
    user,
    isMinVideoLength8sec,
  ]);

  const isDurationEnabled = (duration) => {
    if (!isSubscribed)
      return duration === 5 || (isMinVideoLength8sec && duration === 8);
    if (isBasicEnabled) return duration <= 8;
    if (isProEnabled) return true;
    return false;
  };

  const shouldShowDurationBadge = (duration) => {
    if (duration === 5) return null;
    if (duration === 8) {
      return !isSubscribed && !isMinVideoLength8sec ? "basic" : null;
    }
    if (duration === 10 || duration === 12) {
      return !isProEnabled ? "pro" : null;
    }
    return null;
  };

  const handleDurationClick = (duration) => {
    if (isDurationEnabled(duration) && videoDuration !== duration) {
      setVideoDuration(duration);
      localStorage.setItem("videoDuration", duration);
    }
  };

  const shouldShowVideoQualityBadge = (quality) => {
    if (quality === "LOW") return null;
    if (quality === "MEDIUM") {
      return !isSubscribed ? "basic" : null;
    }
    if (quality === "HIGH") {
      return !isProEnabled ? "pro" : null;
    }
    return null;
  };

  const isVideoQualityEnabled = (quality) => {
    if (quality === "LOW") return true;
    if (quality === "MEDIUM") return isBasicEnabled || isProEnabled;
    if (quality === "HIGH") return isProEnabled;
    return false;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContainer>
        <ModalHeader>
          <HeaderHero>
            <TitleGroup>
              <ModalTitle>Customize Scene</ModalTitle>
              <ModalSubtitle>
                Fine-tune seed, quality, and prompts before generation.
              </ModalSubtitle>
            </TitleGroup>
          </HeaderHero>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {/* Seed — segmented + custom input stacked (input full-width below) */}
          <Section>
            <SectionLabel>Seed</SectionLabel>
            <SegmentedControl>
              <SegmentButton
                $active={seedMode === "random"}
                onClick={() => {
                  setSeedMode("random");
                  localStorage.setItem("seedMode", "random");
                  setCustomSeed(generateRandomSeed());
                }}
              >
                Random
              </SegmentButton>
              <SegmentButton
                $active={seedMode === "default"}
                onClick={() => {
                  setSeedMode("default");
                  localStorage.setItem("seedMode", "default");
                  setCustomSeed(collectionSeed);
                }}
              >
                Default
              </SegmentButton>
              <SegmentButton
                $active={seedMode === "custom"}
                onClick={() => {
                  setSeedMode("custom");
                  localStorage.setItem("seedMode", "custom");
                }}
              >
                Custom
              </SegmentButton>
            </SegmentedControl>
            {seedMode === "custom" && (
              <SeedInput
                type="number"
                placeholder="Enter your seed…"
                value={customSeed}
                min={1}
                max={9007199254740991}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value >= 0 && value <= 9007199254740991) {
                    setCustomSeed(value > 0 ? value : 1);
                    if (seedMode === "custom") {
                      localStorage.setItem("customSeed", value > 0 ? value : 1);
                    }
                  }
                }}
              />
            )}
          </Section>

          {/* Quality — 2-column grid (Resolution | Duration) */}
          {enableVideoQuality && (
            <Section>
              <SectionLabel>Quality</SectionLabel>
              <SettingsCard>
                <QualityGrid>
                  <SettingCol>
                    <SettingLabel>Resolution</SettingLabel>
                    <SegmentedControl>
                      {ALL_VIDEO_RESOLUTIONS.map((type) => {
                        const badge = shouldShowVideoQualityBadge(type);
                        const isEnabled = isVideoQualityEnabled(type);
                        return (
                          <SegmentButton
                            key={type}
                            $active={type === videoResolution}
                            $disabled={!isEnabled}
                            onClick={() => {
                              if (isEnabled) {
                                setVideoResolution(type);
                                localStorage.setItem("videoResolution", type);
                              } else {
                                setOpenUnlockModal(true);
                              }
                            }}
                          >
                            {badge && (
                              <InlineLock>
                                <LockIcon />
                              </InlineLock>
                            )}
                            {type.charAt(0).toUpperCase() +
                              type.slice(1).toLowerCase()}
                            {badge === "basic" && <Badge>Basic</Badge>}
                            {badge === "pro" && <Badge $pro>Pro</Badge>}
                          </SegmentButton>
                        );
                      })}
                    </SegmentedControl>
                  </SettingCol>

                  <SettingCol>
                    <SettingLabel>Duration</SettingLabel>
                    <SegmentedControl>
                      {DURATION_OPTIONS.map((duration) => {
                        const isEnabled = isDurationEnabled(duration);
                        const badge = shouldShowDurationBadge(duration);
                        return (
                          <SegmentButton
                            key={duration}
                            $active={videoDuration === duration}
                            $disabled={!isEnabled}
                            onClick={() => {
                              if (isEnabled) {
                                handleDurationClick(duration);
                              } else {
                                setOpenUnlockModal(true);
                              }
                            }}
                          >
                            {badge && (
                              <InlineLock>
                                <LockIcon />
                              </InlineLock>
                            )}
                            {duration}s
                            {badge === "basic" && <Badge>Basic</Badge>}
                            {badge === "pro" && <Badge $pro>Pro</Badge>}
                          </SegmentButton>
                        );
                      })}
                    </SegmentedControl>
                  </SettingCol>
                </QualityGrid>
              </SettingsCard>
            </Section>
          )}

          {/* Prompts — unified panel: header + custom/negative share one bg */}
          <PromptsPanel $expanded={showPrompts}>
            <PromptsHeader
              $expanded={showPrompts}
              onClick={() => setShowPrompts(!showPrompts)}
            >
              <PromptsLabel>Edit Prompt</PromptsLabel>
              <PromptsChevron $expanded={showPrompts} />
            </PromptsHeader>
            <PromptsContent $expanded={showPrompts}>
              <PromptGroup>
                <PromptLabel>Custom Prompt</PromptLabel>
                <PromptTextarea
                  $minHeight
                  placeholder="Enter your custom prompt here..."
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                />
              </PromptGroup>
              <PromptGroup>
                <PromptLabel>Negative Prompt</PromptLabel>
                <PromptTextarea
                  placeholder="Enter your negative prompt here..."
                  value={negativePrompt}
                  onChange={(e) => setNegativePrompt(e.target.value)}
                />
              </PromptGroup>
            </PromptsContent>
          </PromptsPanel>
        </ModalBody>

        <Footer>
          <TimeBadge>
            Est. time · {VIDEO_RESOLUTION_DESCRIPTION[videoResolution]}
          </TimeBadge>
          <ActionRow>
            <PrimaryButton onClick={() => onClose(false)}>
              Save
              <PrimaryCostGroup>
                <GenerateDrop aria-hidden="true" />
                {totalPrice}
              </PrimaryCostGroup>
            </PrimaryButton>
            <CancelLink type="button" onClick={onClose}>
              Cancel
            </CancelLink>
          </ActionRow>
        </Footer>
      </ModalContainer>
      <UnlockModal
        isOpen={openUnlockModal}
        onClose={() => setOpenUnlockModal(false)}
      />
    </Modal>
  );
};

export default EnterPromptModal;
